import { useCallback, useEffect, useRef, useState } from "react";
import { Trophy, RefreshCw, AlertTriangle, Radio, EyeOff, Copy, Users, Download, CheckCircle2, Webhook, SearchCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { parseUsernames } from "../../lib/publisherTools";

const toolBtn = "flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50";
const toolCyan = `${toolBtn} bg-cyan-400/15 border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/25`;
const toolGray = `${toolBtn} bg-white/5 border border-white/10 text-white hover:bg-white/10`;

const LEAGUES = [
  { label: "A1", classType: 2000 }, { label: "A2", classType: 1900 }, { label: "A3", classType: 1800 },
  { label: "B1", classType: 1500 }, { label: "B2", classType: 1400 }, { label: "B3", classType: 1300 }, { label: "B4", classType: 1200 }, { label: "B5", classType: 1100 },
  { label: "C1", classType: 1000 }, { label: "C2", classType: 900 }, { label: "C3", classType: 800 }, { label: "C4", classType: 700 }, { label: "C5", classType: 600 },
  { label: "D1", classType: 500 }, { label: "D2", classType: 400 }, { label: "D3", classType: 300 }, { label: "D4", classType: 200 }, { label: "D5", classType: 100 },
];

const makeTxt = (names, label) => {
  const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
  const blob = new Blob([names.join("\n")], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tiktok-${(label || "isimler").replace(/[^\w-]+/g, "-")}-${stamp}.txt`;
  a.click();
  URL.revokeObjectURL(url);
};

export default function LeagueRankings() {
  const [leagues, setLeagues] = useState([]);
  const [selected, setSelected] = useState("genel");
  const [selectedLabel, setSelectedLabel] = useState("TR Genel");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [archive, setArchive] = useState([]);
  const [archiveTotal, setArchiveTotal] = useState(0);
  const [downloaded, setDownloaded] = useState([]);
  const [downloadedTotal, setDownloadedTotal] = useState(0);
  const [hooks, setHooks] = useState({});
  const [hooksSaving, setHooksSaving] = useState(false);
  const [lookupText, setLookupText] = useState("");
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupMeta, setLookupMeta] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const reqRef = useRef(0);

  const loadArchive = useCallback(async () => {
    try {
      const r = await api.get("/admin/tiktool/names");
      setArchive(r.data?.names || []);
      setArchiveTotal(r.data?.total ?? 0);
    } catch { /* yok say */ }
  }, []);

  const loadDownloaded = useCallback(async () => {
    try {
      const r = await api.get("/admin/tiktool/names", { params: { scope: "downloaded" } });
      setDownloaded(r.data?.names || []);
      setDownloadedTotal(r.data?.total ?? 0);
    } catch { /* yok say */ }
  }, []);

  const loadHooks = useCallback(async () => {
    try {
      const r = await api.get("/admin/discord/settings");
      setHooks(r.data?.league_webhooks || {});
    } catch { /* yok say */ }
  }, []);

  useEffect(() => {
    api.get("/admin/tiktool/leagues").then((r) => setLeagues(r.data?.leagues || [])).catch(() => setLeagues([]));
    loadArchive();
    loadDownloaded();
    loadHooks();
  }, [loadArchive, loadDownloaded, loadHooks]);

  const load = useCallback(async (which, label) => {
    const target = which ?? selected;
    const myReq = ++reqRef.current;
    setLoading(true);
    setError("");
    try {
      if (target === "genel") {
        const r = await api.get("/admin/tiktool/country");
        if (myReq !== reqRef.current) return;
        const cur = r.data?.current || {};
        setRows((cur.channels || []).map((c) => ({
          rank: c.rank, uniqueId: c.uniqueId, name: c.displayName, score: c.score,
          avatar: c.profilePic ? (c.profilePic.startsWith("http") ? c.profilePic : `https://tik.tools${c.profilePic}`) : null,
          isLive: c.isLive, masked: c.masked,
        })));
        setMeta({ label: "TR Genel Sıralama", count: cur.total_entries, extra: `Canlı: ${cur.live_count ?? "—"} — sıfırlanma: ${r.data?.next_reset_in || "—"}` });
        if (label) setSelectedLabel(label);
      } else {
        const r = await api.get(`/admin/tiktool/league/${target}`);
        if (myReq !== reqRef.current) return;
        setRows((r.data?.entries || []).map((e) => ({
          rank: e.rank, uniqueId: e.uniqueId, name: e.nickname, score: e.score,
          avatar: e.avatar || null, isLive: e.isLive, masked: e.masked,
        })));
        const m = r.data?.maskedCount ? ` — ${r.data.maskedCount} kayıt gizli` : "";
        setMeta({ label: `TR ${r.data?.classLabel || label || ""} Ligi`, count: (r.data?.entries || []).length, extra: m, updatedAt: r.data?.updatedAt });
        if (label) setSelectedLabel(label);
      }
    } catch (e) {
      if (myReq !== reqRef.current) return;
      setRows([]);
      setMeta(null);
      setError(formatApiError(e));
    } finally {
      if (myReq === reqRef.current) setLoading(false);
    }
    loadArchive();
  }, [selected, loadArchive]);

  useEffect(() => { load("genel"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selected !== "genel") return undefined;
    const t = setInterval(() => load("genel"), 30000);
    return () => clearInterval(t);
  }, [selected, load]);

  useEffect(() => {
    if (selected === "genel") return undefined;
    const t = setInterval(() => load(), 3600000);
    return () => clearInterval(t);
  }, [selected, load]);

  const viewNames = () => [...new Set(rows.filter((r) => !r.masked && r.uniqueId).map((r) => r.uniqueId.toLowerCase()))];

  const consumeView = async (mode) => {
    const names = viewNames();
    if (!names.length) {
      toast.error("Bu görünümde alınacak açık isim yok");
      return;
    }
    try {
      const r = await api.post("/admin/tiktool/names/consume", { names });
      const fresh = r.data?.names || [];
      const skipped = r.data?.skipped || 0;
      if (!fresh.length) {
        toast.info(`Yeni isim yok — ${skipped} isim zaten arşivde`);
        return;
      }
      if (mode === "download") {
        makeTxt(fresh, selectedLabel);
        toast.success(`${fresh.length} yeni isim indirildi${skipped ? ` — ${skipped} tanesi zaten arşivdeydi, atlandı` : ""}`);
      } else {
        await navigator.clipboard.writeText(fresh.join("\n"));
        toast.success(`${fresh.length} yeni isim kopyalandı${skipped ? ` — ${skipped} tanesi zaten arşivdeydi, atlandı` : ""}`);
      }
      loadArchive();
      loadDownloaded();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const consumeArchive = async (mode) => {
    if (!archive.length) {
      toast.error("Listede yeni isim yok");
      return;
    }
    try {
      const r = await api.post("/admin/tiktool/names/consume");
      const names = r.data?.names || [];
      setArchive([]);
      setArchiveTotal(0);
      if (mode === "download") {
        makeTxt(names, "yeni-isimler");
        toast.success(`${names.length} isim TXT indirildi — bir daha sunulmaz`);
      } else {
        await navigator.clipboard.writeText(names.join("\n"));
        toast.success(`${names.length} isim kopyalandı — bir daha sunulmaz`);
      }
      loadDownloaded();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const clearArchive = async () => {
    if (!window.confirm("Bekleyen isimler indirilmeden listeden düşülsün mü? (Bir daha gelmezler)")) return;
    try {
      await api.delete("/admin/tiktool/names");
      setArchive([]);
      setArchiveTotal(0);
      toast.success("Bekleyen isimler düşürüldü");
      loadDownloaded();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runLookup = async () => {
    const names = parseUsernames(lookupText);
    if (!names.length) {
      toast.error("Sorgulanacak en az bir kullanıcı adı girin");
      return;
    }
    setLookupLoading(true);
    try {
      const r = await api.post("/admin/tiktool/lookup", { names });
      setLookupResults(r.data?.results || []);
      setLookupMeta({ total: r.data?.total ?? 0, inRanking: r.data?.in_ranking ?? 0 });
      toast.success(`${r.data?.total ?? 0} isim sorgulandı — ${r.data?.in_ranking ?? 0} tanesi güncel sıralamada`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLookupLoading(false);
    }
  };

  const saveHooks = async () => {
    setHooksSaving(true);
    try {
      await api.post("/admin/discord/settings", { league_webhooks: hooks });
      toast.success("Discord webhook ayarları kaydedildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHooksSaving(false);
    }
  };

  const testHook = async (league) => {
    try {
      await api.post("/admin/discord/test", { league });
      toast.success(`${league === "GENEL" ? "TR Genel" : league} kanalına test mesajı gönderildi`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sortedLeagues = LEAGUES.map((l) => {
    const found = leagues.find((x) => x.classType === l.classType);
    return { classType: l.classType, classLabel: l.label, count: found?.count ?? 0, active: Boolean(found) };
  });
  const leagueBtn = (active) =>
    `px-4 py-2 rounded-xl text-sm font-mono font-bold transition-colors border ${
      active ? "bg-cyan-400/20 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/10 text-[#A0ABC0] hover:bg-white/10 hover:text-white"
    }`;

  return (
    <div data-testid="league-rankings-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
          <Trophy size={20} className="text-cyan-300" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">Lig Sıralamaları</h1>
          <p className="text-sm text-[#A0ABC0]">TikTok LIVE Türkiye — lige tıklayın, sıralamayı görün, TXT indirin</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="league-buttons">
        <button data-testid="league-btn-genel" onClick={() => { setSelected("genel"); load("genel", "TR Genel"); }} className={leagueBtn(selected === "genel")}>
          TR Genel
        </button>
        {sortedLeagues.map((l) => (
          <button key={l.classType} data-testid={`league-btn-${l.classLabel}`} onClick={() => { setSelected(String(l.classType)); load(String(l.classType), l.classLabel); }} className={`${leagueBtn(selected === String(l.classType))} ${l.active ? "" : "opacity-40"}`} title={l.active ? `${l.count} yayıncı` : "Şu an TikTok'ta bu ligde veri yok"}>
            {l.classLabel}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 bg-white/5 border border-white/10 rounded-2xl p-4">
        <span className="text-sm font-semibold text-white">{selectedLabel}</span>
        <button data-testid="league-fetch-button" onClick={() => load()} disabled={loading} className={toolCyan}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Yükleniyor..." : "Yenile"}
        </button>
        <button data-testid="view-download-button" onClick={() => consumeView("download")} disabled={!rows.length || loading} className={toolCyan}>
          <Download size={14} /> TXT İndir
        </button>
        <button data-testid="view-copy-button" onClick={() => consumeView("copy")} disabled={!rows.length || loading} className={toolGray}>
          <Copy size={14} /> Kopyala
        </button>
      </div>

      {error && (
        <div data-testid="league-error-box" className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-2xl p-4">
          <AlertTriangle size={18} className="text-red-400 mt-0.5 shrink-0" />
          <p className="text-sm text-red-200">{error}</p>
        </div>
      )}

      {meta && !error && (
        <p data-testid="league-status-text" className="text-xs text-[#A0ABC0]">
          {meta.label} — {meta.count} kayıt{meta.extra || ""}
          {meta.updatedAt ? ` — güncelleme: ${new Date(meta.updatedAt).toLocaleTimeString("tr-TR")}` : ""}
        </p>
      )}

      {loading && (
        <p data-testid="league-loading" className="text-sm text-cyan-300 animate-pulse">Sıralama yükleniyor, lig verileri birkaç saniye sürebilir…</p>
      )}

      {rows.length > 0 && (
        <div className={`overflow-x-auto rounded-2xl border border-white/10 transition-opacity ${loading ? "opacity-40" : ""}`}>
          <table data-testid="league-table" className="w-full text-sm">
            <thead>
              <tr className="bg-white/5 text-[#A0ABC0] text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 w-16">Sıra</th>
                <th className="text-left px-4 py-3">Yayıncı</th>
                <th className="text-left px-4 py-3 w-20">Durum</th>
                <th className="text-right px-4 py-3">Skor</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x, i) => (
                <tr key={`${x.uniqueId}-${i}`} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`league-row-${i}`}>
                  <td className="px-4 py-3 font-mono text-cyan-300">{x.rank ?? i + 1}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {x.avatar && <img src={x.avatar} alt="" className="w-7 h-7 rounded-full object-cover bg-white/10" loading="lazy" />}
                      <div>
                        <div className="text-white font-medium flex items-center gap-1.5">
                          {x.masked ? <span className="inline-flex items-center gap-1 text-[#64748B]"><EyeOff size={12} /> gizli</span> : (x.name || "—")}
                        </div>
                        <div className="text-xs text-[#64748B] font-mono">{x.masked ? "" : `@${x.uniqueId || ""}`}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {x.isLive ? (
                      <span className="inline-flex items-center gap-1 text-xs text-red-400" data-testid={`league-live-${i}`}><Radio size={12} /> Canlı</span>
                    ) : (
                      <span className="text-xs text-[#475569]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-white">{typeof x.score === "number" ? x.score.toLocaleString("tr-TR") : (x.score ?? "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <p data-testid="league-empty-text" className="text-sm text-[#64748B]">Kayıt bulunamadı.</p>
      )}

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3" data-testid="lookup-section">
        <div className="flex items-center gap-2">
          <SearchCheck size={16} className="text-cyan-300" />
          <h2 className="text-sm font-semibold text-white">Toplu Skor Sorgu</h2>
        </div>
        <p className="text-xs text-[#64748B]">Kullanıcı adlarını toplu yapıştırın (her satıra bir tane). Hâlâ güncel sıralamadaysa güncel skorunu ve ligini görürsünüz; değilse sistemdeki son bilinen skoru gösterilir. Sonuçlar yüksekten düşüğe sıralanır.</p>
        <textarea
          value={lookupText}
          onChange={(e) => setLookupText(e.target.value)}
          rows={4}
          placeholder={"kajapubg\noykkcm\ns2gsloxer"}
          data-testid="lookup-input"
          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-[#475569] font-mono text-xs outline-none focus:border-cyan-400/60 transition-colors resize-y"
        />
        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="lookup-run-button" onClick={runLookup} disabled={lookupLoading} className={toolCyan}>
            {lookupLoading ? <RefreshCw size={14} className="animate-spin" /> : <SearchCheck size={14} />}
            {lookupLoading ? "Ligler taranıyor..." : "Skorları Sorgula"}
          </button>
          {lookupMeta && (
            <span data-testid="lookup-meta" className="text-xs text-[#A0ABC0]">
              {lookupMeta.total} isim — <span className="text-emerald-300 font-semibold">{lookupMeta.inRanking} tanesi güncel sıralamada</span>
            </span>
          )}
        </div>
        {lookupResults.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="lookup-results">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/5 text-[#A0ABC0] text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5 w-12">#</th>
                  <th className="text-left px-4 py-2.5">Kullanıcı</th>
                  <th className="text-right px-4 py-2.5">Güncel Skor</th>
                  <th className="text-left px-4 py-2.5">Lig</th>
                  <th className="text-left px-4 py-2.5">Durum</th>
                  <th className="text-right px-4 py-2.5">Son Bilinen Skor</th>
                </tr>
              </thead>
              <tbody>
                {lookupResults.map((r, i) => (
                  <tr key={r.username} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`lookup-row-${r.username}`}>
                    <td className="px-4 py-2.5 font-mono text-cyan-300">{i + 1}</td>
                    <td className="px-4 py-2.5 font-mono text-white">@{r.username}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-white">
                      {typeof r.current_score === "number" ? r.current_score.toLocaleString("tr-TR") : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-cyan-200">{r.current_league ? (r.current_league === "GENEL" ? "TR Genel" : `${r.current_league}`) : "—"}</td>
                    <td className="px-4 py-2.5 text-xs">
                      {r.in_ranking ? (
                        <span className="inline-flex items-center gap-1.5 text-emerald-300" data-testid={`lookup-status-${r.username}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Sıralamada
                          {r.is_live && <span className="inline-flex items-center gap-1 text-red-400"><Radio size={11} /> Canlı</span>}
                        </span>
                      ) : r.seen_before ? (
                        <span className="inline-flex items-center gap-1.5 text-amber-300" data-testid={`lookup-status-${r.username}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> Listeden düşmüş
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[#64748B]" data-testid={`lookup-status-${r.username}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-[#475569]" /> Hiç görülmedi
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-[#A0ABC0]">
                      {typeof r.last_score === "number" ? `${r.last_score.toLocaleString("tr-TR")}${r.last_league ? ` (${r.last_league})` : ""}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3" data-testid="archive-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-cyan-300" />
            <h2 className="text-sm font-semibold text-white">Yeni İsimler</h2>
            <span data-testid="archive-count" className="text-xs font-mono text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-2 py-0.5">{archiveTotal}</span>
          </div>
          <div className="flex gap-2">
            <button data-testid="archive-download-button" onClick={() => consumeArchive("download")} disabled={!archive.length} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-400/15 border border-cyan-400/30 text-cyan-300 text-xs font-medium hover:bg-cyan-400/25 transition-colors disabled:opacity-50">
              <Download size={12} /> TXT İndir
            </button>
            <button data-testid="archive-copy-button" onClick={() => consumeArchive("copy")} disabled={!archive.length} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs font-medium hover:bg-white/10 transition-colors disabled:opacity-50">
              <Copy size={12} /> Kopyala
            </button>
            <button data-testid="archive-clear-button" onClick={clearArchive} disabled={!archive.length} className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50">
              Düşür
            </button>
          </div>
        </div>
        <p className="text-xs text-[#64748B]">Her saat yeni isimler burada birikir. İndirdiğinizde veya kopyaladığınızda listeden düşerler ve bir daha sunulmazlar.</p>
        <div data-testid="archive-list" className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
          {archive.map((n) => (
            <span key={n.unique_id} className="text-xs font-mono text-[#A0ABC0] bg-black/40 border border-white/10 rounded-lg px-2 py-1">@{n.unique_id}</span>
          ))}
          {!archive.length && <span className="text-xs text-[#475569]">Bekleyen yeni isim yok — saatlik toplamada ve sıralama görüntülemelerinde yeni isimler burada birikir.</span>}
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3" data-testid="downloaded-section">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} className="text-emerald-400" />
          <h2 className="text-sm font-semibold text-white">İndirilen İsimler</h2>
          <span data-testid="downloaded-count" className="text-xs font-mono text-emerald-300 bg-emerald-400/10 border border-emerald-400/20 rounded-full px-2 py-0.5">{downloadedTotal}</span>
        </div>
        <p className="text-xs text-[#64748B]">Bu isimler daha önce indirildi/kopyalandı — hangi ligde görülürse görülsün size bir daha sunulmazlar.</p>
        <div data-testid="downloaded-list" className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
          {downloaded.map((n) => (
            <span key={n.unique_id} className="text-xs font-mono text-[#64748B] bg-black/40 border border-white/5 rounded-lg px-2 py-1 line-through decoration-white/20">@{n.unique_id}</span>
          ))}
          {!downloaded.length && <span className="text-xs text-[#475569]">Henüz indirilen isim yok.</span>}
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3" data-testid="discord-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Webhook size={16} className="text-indigo-300" />
            <h2 className="text-sm font-semibold text-white">Discord Webhook Ayarları</h2>
          </div>
          <button data-testid="discord-save-button" onClick={saveHooks} disabled={hooksSaving} className="px-4 py-1.5 rounded-lg bg-cyan-400/15 border border-cyan-400/30 text-cyan-300 text-xs font-medium hover:bg-cyan-400/25 transition-colors disabled:opacity-50">
            {hooksSaving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
        <p className="text-xs text-[#64748B]">Her ligin Discord kanalının webhook URL'sini yapıştırın. 30 yeni isim dolunca o kanala otomatik gönderilir; daha önce indirilen veya gönderilen isimler asla tekrar gönderilmez. İsimler kod bloğunda gelir — Discord'da bloğun sağ üstündeki simgeyle tek tıkla kopyalanır.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {["GENEL", ...sortedLeagues.map((l) => l.classLabel)].map((lg) => (
            <div key={lg} className="flex items-center gap-2">
              <span className="w-16 shrink-0 text-xs font-mono font-bold text-cyan-300">{lg === "GENEL" ? "TR Genel" : lg}</span>
              <input
                data-testid={`discord-input-${lg}`}
                value={hooks[lg] || ""}
                onChange={(e) => setHooks((h) => ({ ...h, [lg]: e.target.value }))}
                placeholder="https://discord.com/api/webhooks/..."
                className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white font-mono outline-none focus:border-cyan-400/60 transition-colors"
              />
              <button data-testid={`discord-test-${lg}`} onClick={() => testHook(lg)} className="shrink-0 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[#A0ABC0] text-xs hover:bg-white/10 transition-colors">Test</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
